import React, { useCallback, useEffect, useMemo, useState } from "react";
import { BookOpen, ChevronRight, Loader2, Sparkles, WalletCards } from "lucide-react";
import { getMyPayslips } from "../../../api/hrm/payroll/payroll";
import { showErrorToast } from "../../../components/ConfirmDialog";
import MyPayslipFilter from "./MyPayslipFilter";
import MyPayslipMiniDashboard from "./MyPayslipMiniDashboard";
import MyPayslipDetailModal from "./MyPayslipDetailModal";
import { startMyPayslipTour } from "./myPayslipTour";
import {
  getMockMyPayslips,
  isPayrollMockForced,
  shouldUsePayrollMock,
} from "./payrollMockData";
import {
  formatCurrency,
  formatDateTime,
  formatMonthLabel,
  formatWorkDays,
} from "./payslipFormat";

const openGuide = (path) => {
  const base = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
  window.open(`${base}${path}`, "_blank", "noopener,noreferrer");
};

export default function MyPayslipPage() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [usingMock, setUsingMock] = useState(false);
  const [filters, setFilters] = useState({ keyword: "", year: "", month: "" });
  const [selectedPayslip, setSelectedPayslip] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      if (isPayrollMockForced()) {
        setRecords(getMockMyPayslips());
        setUsingMock(true);
        return;
      }
      const res = await getMyPayslips();
      if (shouldUsePayrollMock(res)) {
        setRecords(getMockMyPayslips());
        setUsingMock(true);
      } else {
        setRecords(res || []);
        setUsingMock(false);
      }
    } catch (err) {
      console.error(err);
      setRecords(getMockMyPayslips());
      setUsingMock(true);
      showErrorToast(err.response?.data?.message || "Không tải được API — đang xem dữ liệu mẫu.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const yearOptions = useMemo(() => {
    const years = new Set(
      records.map((row) => String(row.year_month || "").split("-")[0]).filter(Boolean)
    );
    return Array.from(years).sort((a, b) => b.localeCompare(a));
  }, [records]);

  const visibleRecords = useMemo(() => {
    const keyword = filters.keyword.trim().toLowerCase();
    return records.filter((row) => {
      const [year, month] = String(row.year_month || "").split("-");
      if (filters.year && year !== filters.year) return false;
      if (filters.month && month !== filters.month) return false;
      if (!keyword) return true;
      return [row.year_month, row.run_code, formatMonthLabel(row.year_month)]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(keyword));
    });
  }, [records, filters]);

  const stats = useMemo(() => {
    const latest = visibleRecords[0];
    return {
      count: visibleRecords.length,
      latestNet: latest?.net_pay || 0,
      latestLabel: latest ? formatMonthLabel(latest.year_month) : "Chưa có dữ liệu",
      totalNet: visibleRecords.reduce((sum, row) => sum + Number(row.net_pay || 0), 0),
      totalOvertime: visibleRecords.reduce((sum, row) => sum + Number(row.overtime_amount || 0), 0),
      totalOvertimeMinutes: visibleRecords.reduce(
        (sum, row) => sum + Number(row.overtime_minutes || 0),
        0
      ),
    };
  }, [visibleRecords]);

  const renderEmptyState = () => (
    <div className="rounded-[2rem] border border-dashed border-gray-200 bg-white p-10 text-center shadow-sm">
      <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-gray-50 text-gray-400">
        <WalletCards size={22} />
      </div>
      <p className="text-sm font-bold text-gray-700">Chưa có phiếu lương nào được công bố</p>
      <p className="mt-1 text-xs text-gray-400">
        Phiếu lương chỉ hiển thị sau khi CEO duyệt và Kế toán công bố kỳ lương.
      </p>
    </div>
  );

  return (
    <div className="w-full min-h-screen bg-gray-50/30 p-3 pb-24 md:p-8 md:pb-8">
      <div className="mb-4 flex flex-col gap-3 sm:mb-6 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div data-tour="hrm-my-payslip-header">
          <div className="mb-1 flex items-center gap-3 sm:mb-2 sm:gap-4">
            <div className="rounded-2xl border border-red-700/10 bg-red-700/5 p-3 text-red-700 shadow-sm sm:rounded-[1.5rem] sm:p-4">
              <WalletCards className="h-5 w-5 sm:h-6 sm:w-6" />
            </div>
            <div className="min-w-0">
              <h1 className="text-xl font-black uppercase tracking-tight leading-tight text-gray-800 sm:text-2xl">
                Phiếu lương <span className="text-red-700">cá nhân</span>
              </h1>
              <p className="mt-0.5 text-[10px] font-bold uppercase tracking-[0.16em] text-gray-400 sm:mt-1 sm:tracking-[0.2em]">
                Chỉ hiển thị sau khi CEO duyệt và công bố chính thức
              </p>
            </div>
          </div>
        </div>

        <div className="flex shrink-0 flex-wrap items-center gap-2 self-start sm:self-auto">
          <button
            type="button"
            onClick={() => window.setTimeout(() => startMyPayslipTour(), 80)}
            className="inline-flex items-center justify-center gap-2 rounded-2xl border border-red-200 bg-red-50/90 px-4 py-2.5 text-xs font-bold uppercase tracking-wide text-red-800 shadow-sm transition hover:border-red-300 hover:bg-red-100"
          >
            <Sparkles className="h-4 w-4 text-red-600" strokeWidth={2.25} />
            Production tour
          </button>
          <button
            type="button"
            onClick={() => openGuide("/guides/hrm-my-payslip-guide.html")}
            className="inline-flex items-center justify-center gap-2 rounded-2xl border border-gray-200 bg-white px-4 py-2.5 text-xs font-bold uppercase tracking-wide text-gray-700 shadow-sm transition hover:border-red-300 hover:bg-red-50 hover:text-red-700"
          >
            <BookOpen className="h-4 w-4" strokeWidth={2.25} />
            Documentation
          </button>
        </div>
      </div>

      {usingMock && (
        <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Đang xem <strong>dữ liệu mẫu</strong> để thử UI. Thêm{" "}
          <code className="rounded bg-amber-100 px-1.5 py-0.5 text-xs">?mock=1</code> vào URL để
          luôn dùng mẫu; khi API có phiếu lương thật, trang sẽ tự chuyển sang dữ liệu thật.
        </div>
      )}

      <div data-tour="hrm-my-payslip-dashboard">
        <MyPayslipMiniDashboard stats={stats} />
      </div>

      <div data-tour="hrm-my-payslip-filter">
        <MyPayslipFilter
          onFilterChange={setFilters}
          onRefresh={fetchData}
          yearOptions={yearOptions}
        />
      </div>

      <div data-tour="hrm-my-payslip-list">
      {loading ? (
        <div className="flex justify-center rounded-[2rem] border border-gray-100 bg-white py-16 shadow-sm">
          <Loader2 className="animate-spin text-red-600" size={30} />
        </div>
      ) : visibleRecords.length === 0 ? (
        renderEmptyState()
      ) : (
        <>
          {/* Mobile: card list */}
          <div className="space-y-3 md:hidden">
            {visibleRecords.map((row) => (
              <button
                key={row.id}
                type="button"
                onClick={() => setSelectedPayslip(row)}
                className="w-full rounded-2xl border border-gray-100 bg-white p-4 text-left shadow-sm transition active:scale-[0.99]"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-black uppercase tracking-tight text-gray-800">
                      {formatMonthLabel(row.year_month)}
                    </p>
                    <p className="mt-1 text-xs text-gray-400">{row.run_code || "—"}</p>
                  </div>
                  <span className="shrink-0 rounded-full border border-violet-200 bg-violet-50 px-2.5 py-1 text-[10px] font-black uppercase text-violet-700">
                    Đã công bố
                  </span>
                </div>
                <div className="mt-3 flex items-end justify-between gap-3">
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">
                      Thực lĩnh
                    </p>
                    <p className="text-lg font-black text-red-700 tabular-nums">
                      {formatCurrency(row.net_pay)}
                    </p>
                  </div>
                  <span className="flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-gray-400">
                    Chi tiết
                    <ChevronRight size={14} />
                  </span>
                </div>
              </button>
            ))}
          </div>

          {/* Desktop: table */}
          <div className="hidden overflow-hidden rounded-[2rem] border border-gray-100 bg-white shadow-sm md:block">
            <table className="min-w-full border-separate border-spacing-0">
              <thead className="bg-slate-50/80">
                <tr>
                  <th className="min-w-[180px] border-b border-gray-100 p-5 text-left text-[11px] font-black uppercase tracking-widest text-slate-400">
                    Kỳ lương
                  </th>
                  <th className="min-w-[140px] border-b border-gray-100 p-5 text-center text-[11px] font-black uppercase tracking-widest text-slate-400">
                    Ngày công
                  </th>
                  <th className="min-w-[150px] border-b border-gray-100 p-5 text-right text-[11px] font-black uppercase tracking-widest text-slate-400">
                    Tăng ca
                  </th>
                  <th className="min-w-[160px] border-b border-gray-100 p-5 text-right text-[11px] font-black uppercase tracking-widest text-slate-400">
                    Tổng thu nhập
                  </th>
                  <th className="min-w-[160px] border-b border-gray-100 p-5 text-right text-[11px] font-black uppercase tracking-widest text-slate-400">
                    Thực lĩnh
                  </th>
                  <th className="min-w-[150px] border-b border-gray-100 p-5 text-center text-[11px] font-black uppercase tracking-widest text-slate-400">
                    Ngày công bố
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {visibleRecords.map((row) => (
                  <tr
                    key={row.id}
                    role="button"
                    tabIndex={0}
                    title="Xem chi tiết phiếu lương"
                    onClick={() => setSelectedPayslip(row)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setSelectedPayslip(row);
                      }
                    }}
                    className="cursor-pointer transition-colors hover:bg-red-50/40"
                  >
                    <td className="p-5">
                      <p className="text-sm font-black uppercase tracking-tight text-gray-800">
                        {formatMonthLabel(row.year_month)}
                      </p>
                      <p className="mt-1 text-xs text-gray-400">{row.run_code || "—"}</p>
                    </td>
                    <td className="p-5 text-center text-sm font-semibold text-gray-700 tabular-nums">
                      {formatWorkDays(row.actual_work_days)} / {formatWorkDays(row.standard_work_days)}
                    </td>
                    <td className="p-5 text-right text-sm font-semibold text-gray-700 tabular-nums">
                      {formatCurrency(row.overtime_amount)}
                    </td>
                    <td className="p-5 text-right text-sm font-semibold text-gray-700 tabular-nums">
                      {formatCurrency(row.gross_pay)}
                    </td>
                    <td className="p-5 text-right text-base font-black text-red-700 tabular-nums">
                      {formatCurrency(row.net_pay)}
                    </td>
                    <td className="p-5 text-center text-xs font-semibold text-gray-500">
                      {formatDateTime(row.published_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      </div>

      <MyPayslipDetailModal
        isOpen={Boolean(selectedPayslip)}
        payslip={selectedPayslip}
        onClose={() => setSelectedPayslip(null)}
      />
    </div>
  );
}
